import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { motion } from 'framer-motion';
import { Sparkle, Drop, CaretLeft, Check, Fire, ChatTeardropDots } from '@phosphor-icons/react';
import BottomNav from '../components/BottomNav';
import BeforeAfterSlider from '../components/BeforeAfterSlider';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Iter24 F1+F2 — shared template for Liquid Glass and Detailing i Plotë landing pages.
 * Routes: /liquid-glass (category=liquid_glass), /detailing-i-plote (category=detailing_i_plote).
 *
 * Props: category ∈ {liquid_glass, detailing_i_plote}
 */
export default function PremiumServicePage({ category }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language } = useLanguage();
  const isLiquidGlass = category === 'liquid_glass';
  const accent = isLiquidGlass ? '#FF5A00' : '#A78BFA';
  const HeroIcon = isLiquidGlass ? Sparkle : Drop;

  const [services, setServices] = useState([]);
  const [enabled, setEnabled] = useState(true);
  const [landing, setLanding] = useState(null);
  const [scarcity, setScarcity] = useState(null);
  const [premiumCurrency, setPremiumCurrency] = useState({ premium_currency: 'EUR', eur_to_lek_rate: 98 });

  const [step, setStep] = useState(0);
  const [selectedSvc, setSelectedSvc] = useState(null);
  const [date, setDate] = useState('');
  const [slots, setSlots] = useState([]);
  const [closed, setClosed] = useState(false);  // Iter28 FIX 2 — closed-day flag from backend
  const [time, setTime] = useState(null);
  const [plate, setPlate] = useState('');
  const [notes, setNotes] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [s, settings, lc, sc, pc] = await Promise.all([
          axios.get(`${API}/detailing/services`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API}/settings/public`),
          axios.get(`${API}/detailing/landing-config?category=${category}`).catch(() => ({ data: null })),
          // Iter25 E — dynamic scarcity (total + remaining)
          axios.get(`${API}/scarcity/premium?category=${category}`).catch(() => ({ data: null })),
          axios.get(`${API}/business-settings/premium-currency`).catch(() => ({ data: { premium_currency: 'EUR', eur_to_lek_rate: 98 } })),
        ]);
        const filtered = (s.data || []).filter((x) => x.active !== false && (
          isLiquidGlass ? x.category !== 'detailing_i_plote' : x.category === 'detailing_i_plote'
        ));
        // Sort by price ascending
        filtered.sort((a, b) => (a.price_eur || 0) - (b.price_eur || 0));
        setServices(filtered);
        const masterEnabled = isLiquidGlass
          ? settings.data?.detailing_enabled
          : settings.data?.detailing_i_plote_enabled;
        setEnabled(!!masterEnabled);
        setLanding(lc.data);
        setScarcity(sc.data);
        setPremiumCurrency(pc.data);
      } catch (err) { console.warn('[PremiumService] load failed', err?.response?.status || err?.message); }
    })();
  }, [category, isLiquidGlass]);

  useEffect(() => {
    if (user) {
      if (!name) setName(user.name || '');
      if (!phone) setPhone(user.phone || '');
    }
  }, [user, name, phone]);

  useEffect(() => {
    if (step === 1 && selectedSvc && date) {
      (async () => {
        try {
          const r = await axios.get(
            `${API}/bookings/availability?service_id=${selectedSvc.service_id}&date=${date}`,
            { withCredentials: true });
          setSlots(r.data?.slots || []);
          setClosed(!!r.data?.closed);
        } catch { setSlots([]); setClosed(false); }
      })();
    }
  }, [step, selectedSvc, date]);

  const startBooking = (svc) => {
    if (!user) {
      toast.info('Ju lutem hyni për të rezervuar.');
      navigate('/login');
      return;
    }
    setSelectedSvc(svc);
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setDate(tomorrow.toISOString().slice(0, 10));
    setStep(1);
  };

  const submit = async () => {
    if (!plate.trim() || !time || !name.trim() || !phone.trim()) {
      toast.error('Plotëso të gjitha fushat');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, {
        customer_name: name.trim(),
        customer_phone: phone.trim(),
        vehicle_plate: plate.toUpperCase().replace(/\s+/g, ''),
        service_id: selectedSvc.service_id,
        scheduled_at: time,
        notes: notes || null,
      }, { withCredentials: true });
      toast.success('Rezervimi u konfirmua!');
      navigate('/account');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gabim');
    } finally { setSubmitting(false); }
  };

  // Display price helper based on premium_currency (G1/G2).
  const displayPrice = (svc, isOriginal = false) => {
    const eur = isOriginal ? svc.original_price_eur : svc.price_eur;
    if (!eur) return null;
    if (premiumCurrency.premium_currency === 'LEK') {
      const lek = Math.round(eur * (premiumCurrency.eur_to_lek_rate || 98));
      return `${lek.toLocaleString(language === 'sq' ? 'sq-AL' : 'en-US')} L`;
    }
    return `€${eur}`;
  };

  if (!enabled) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6">
        <p className="text-center text-white/60 text-sm" data-testid={`${category}-disabled`}>
          Shërbimi nuk është aktiv momentalisht.
        </p>
      </div>
    );
  }

  const heroTitle = landing?.hero_title || (isLiquidGlass ? 'Liquid Glass — detailing premium' : 'Detailing i Plotë');
  const yearsValue = String(landing?.protection_years || 3);
  const heroSubtitle = (landing?.hero_subtitle || (isLiquidGlass ? 'Shkëlqim 3 vjeçar. Efekt pasqyre. Vetëm me rezervim.' : 'Pastrim profesional brendësi + jashtësi + motor.'))
    .replace('{years}', yearsValue);
  const bullets = (landing?.bullets || []).map((b) => b.replace('{years}', yearsValue));
  const ppfEnabled = isLiquidGlass && landing?.ppf_enabled !== false;
  // Iter34 — the "nga vetëm X€" price always mirrors the cheapest active service.
  const minPriceEur = landing?.min_price_eur ?? (services.length ? Math.min(...services.map((s) => s.price_eur)) : null);
  const ppfText = (landing?.ppf_text || `PPF tradicional kushton 3.000-5.000€. Liquid Glass bën të njëjtën gjë nga ${minPriceEur ?? '—'}€.`)
    .replace('{min_price}', String(minPriceEur ?? '—'));
  const showcaseImages = landing?.showcase_images || [];
  const testimonials = landing?.testimonials || [];
  const beforeAfterPairs = landing?.before_after_pairs || [];

  return (
    <div className="min-h-screen bg-[#050505] text-white pb-24" data-testid={`${category}-page`}>
      {/* Header */}
      <div className="px-4 pt-8 pb-4 flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => step > 0 ? setStep(step - 1) : navigate('/')}
                className="h-9 w-9 rounded-xl text-white/70 hover:bg-white/5" data-testid={`${category}-back`}>
          <CaretLeft size={20} />
        </Button>
        <div>
          <h1 className="text-xl font-bold tracking-tight font-['Outfit'] flex items-center gap-2">
            <HeroIcon size={20} weight="fill" style={{ color: accent }} />
            {heroTitle}
          </h1>
          <p className="text-xs text-white/45">{heroSubtitle}</p>
        </div>
      </div>

      {/* Step indicator */}
      <div className="px-4 mb-4 flex items-center gap-1">
        {[0, 1, 2].map((s) => (
          <div key={s} className="h-1 rounded-full flex-1"
               style={{ background: s <= step ? accent : 'rgba(255,255,255,0.1)' }} />
        ))}
      </div>

      {/* Iter25 E — Dynamic scarcity: total weekly slots + remaining */}
      {step === 0 && scarcity && (
        <div className="px-4 mb-3">
          {(() => {
            const remaining = scarcity.remaining ?? scarcity.free ?? 0;
            const total = scarcity.total_slots ?? 29;
            const urgent = remaining <= 8;
            return (
              <div className={`p-2.5 rounded-xl flex items-center gap-2 text-xs ${
                urgent ? 'bg-red-500/15 border border-red-500/30 text-red-400' : 'bg-white/5 border border-white/10'
              }`} data-testid={`${category}-scarcity`}
                   style={!urgent ? { color: accent } : {}}>
                <Fire size={14} weight="fill" />
                <span data-testid={`${category}-scarcity-text`}>
                  <strong>{total}</strong> vende javore — kanë mbetur vetëm{' '}
                  <strong data-testid={`${category}-scarcity-remaining`}>{remaining}</strong> të lira!
                </span>
              </div>
            );
          })()}
        </div>
      )}

      {step === 0 && (
        <div className="px-4 space-y-3" data-testid={`${category}-step-services`}>
          {/* Showcase */}
          {showcaseImages.length > 0 && (
            <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
              {showcaseImages.map((url) => (
                <div key={url} className="snap-center shrink-0 w-64 h-40 rounded-2xl overflow-hidden bg-[#0a0a0a] border border-white/5">
                  <img src={url} alt="" className="w-full h-full object-cover" loading="lazy" />
                </div>
              ))}
            </div>
          )}

          {/* Bullets */}
          {bullets.length > 0 && (
            <div className="p-3 rounded-2xl bg-[#0a0a0a] border border-white/5">
              <ul className="space-y-1.5" data-testid={`${category}-bullets`}>
                {bullets.map((b, i) => (
                  <li key={`${i}-${b}`} className="text-xs text-white/75 flex items-start gap-2">
                    <Check size={12} weight="bold" style={{ color: accent }} className="mt-0.5 shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Before/After slider (Iter24 F3) */}
          {beforeAfterPairs.length > 0 && beforeAfterPairs[0]?.before && (
            <div data-testid={`${category}-before-after`}>
              <p className="text-xs uppercase tracking-wider text-white/45 mb-2">Para & Pas</p>
              <BeforeAfterSlider
                beforeUrl={beforeAfterPairs[0].before}
                afterUrl={beforeAfterPairs[0].after}
              />
            </div>
          )}

          {/* PPF (Liquid Glass only) */}
          {ppfEnabled && (
            <div className="p-3 rounded-2xl bg-gradient-to-br from-[#FFD700]/10 to-transparent border border-[#FFD700]/20"
                 data-testid={`${category}-ppf`}>
              <p className="text-xs text-[#FFD700] leading-relaxed">{ppfText}</p>
            </div>
          )}

          <p className="text-xs uppercase tracking-wider text-white/45 mb-1 pt-2">Zgjidh paketën</p>
          {services.length === 0 ? (
            <p className="text-center text-white/40 text-sm py-8">—</p>
          ) : services.map((svc, idx) => (
            <motion.div key={svc.service_id}
                        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.08 }}
                        className="p-4 rounded-2xl border"
                        style={svc.is_featured
                          ? { background: `linear-gradient(135deg, ${accent}1A 0%, rgba(10,186,181,0.1) 100%)`, borderColor: `${accent}4D` }
                          : { background: '#0a0a0a', borderColor: 'rgba(255,255,255,0.05)' }}
                        data-testid={`${category}-card-${svc.code}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-base font-semibold">{svc.name}</p>
                    {svc.is_featured && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full text-white uppercase tracking-wider"
                            style={{ background: accent }}
                            data-testid={`${category}-featured-badge`}>
                        Më i zgjedhuri
                      </span>
                    )}
                  </div>
                  {svc.description && (
                    <p className="text-xs text-white/55 leading-relaxed">{svc.description}</p>
                  )}
                </div>
                <div className="text-right shrink-0">
                  {svc.original_price_eur > svc.price_eur && (
                    <p className="text-xs text-white/40 line-through" data-testid={`${category}-card-${svc.code}-original`}>
                      {displayPrice(svc, true)}
                    </p>
                  )}
                  <p className="text-2xl font-bold" style={{ color: accent }}>
                    {displayPrice(svc)}
                  </p>
                </div>
              </div>
              {svc.benefits && svc.benefits.length > 0 && (
                <ul className="space-y-1 mb-3">
                  {svc.benefits.map((b, i) => (
                    <li key={`${i}-${b}`} className="text-xs text-white/65 flex items-start gap-1.5">
                      <Check size={12} weight="bold" className="text-[#0ABAB5] mt-0.5" />
                      {b.replace(/_/g, ' ')}
                    </li>
                  ))}
                </ul>
              )}
              <Button onClick={() => startBooking(svc)} data-testid={`${category}-book-${svc.code}`}
                      className="cash-cta-animated w-full text-white h-10 rounded-xl text-sm font-semibold"
                      style={{ background: accent }}>
                Rezervo tani
              </Button>
            </motion.div>
          ))}

          {/* Testimonials */}
          {testimonials.length > 0 && (
            <div className="pt-3 space-y-2" data-testid={`${category}-testimonials`}>
              <p className="text-xs uppercase tracking-wider text-white/45">Çfarë thonë klientët</p>
              {testimonials.map((tm, i) => (
                <div key={`${i}-${tm.text?.slice(0, 24)}`} className="p-3 rounded-2xl bg-[#0a0a0a] border border-white/5">
                  <div className="flex items-start gap-2">
                    <ChatTeardropDots size={14} weight="duotone" style={{ color: accent }} className="mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs text-white/80 italic leading-relaxed">&ldquo;{tm.text}&rdquo;</p>
                      <p className="text-[10px] text-white/40 mt-1">— {tm.name}{tm.city ? `, ${tm.city}` : ''}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {step === 1 && selectedSvc && (
        <div className="px-4 space-y-3" data-testid={`${category}-step-datetime`}>
          <div className="p-3 rounded-2xl bg-[#0a0a0a] border border-white/5">
            <p className="text-xs text-white/45 uppercase tracking-wider mb-1">Zgjidh datën</p>
            <Input type="date" value={date} onChange={(e) => { setDate(e.target.value); setTime(null); }}
                   min={new Date().toISOString().slice(0, 10)}
                   className="h-11 bg-[#050505] border-white/10 rounded-xl text-sm"
                   data-testid={`${category}-date`} />
          </div>
          <div>
            <p className="text-xs text-white/45 uppercase tracking-wider mb-2">Zgjidh orën</p>
            {closed ? (
              <div className="p-4 rounded-2xl bg-[#0a0a0a] border border-red-500/30 text-center" data-testid={`${category}-day-closed`}>
                <p className="text-sm text-red-300 font-semibold">Mbyllur këtë ditë</p>
                <p className="text-xs text-white/55 mt-1">Zgjidh një ditë tjetër — orari i punës caktohet nga menaxheri.</p>
              </div>
            ) : (
              <div className="grid grid-cols-4 gap-2">
                {slots.map((s) => (
                  <button key={s.scheduled_at} disabled={!s.available}
                          onClick={() => setTime(s.scheduled_at)}
                          data-testid={`${category}-slot-${s.scheduled_at.slice(11, 16)}`}
                          className={`h-10 rounded-xl text-sm font-medium ${
                            time === s.scheduled_at
                              ? 'text-white'
                              : s.available
                                ? 'bg-[#0a0a0a] border border-white/10 text-white'
                                : 'bg-[#0a0a0a]/40 text-white/20 cursor-not-allowed line-through'
                          }`}
                          style={time === s.scheduled_at ? { background: accent } : {}}>
                    {s.scheduled_at.slice(11, 16)}
                  </button>
                ))}
              </div>
            )}
          </div>
          <Button onClick={() => setStep(2)} disabled={!time} data-testid={`${category}-next-btn`}
                  className="w-full bg-[#0ABAB5] text-black hover:bg-[#0ABAB5]/90 h-11 rounded-xl">
            Vazhdo
          </Button>
        </div>
      )}

      {step === 2 && selectedSvc && (
        <div className="px-4 space-y-3" data-testid={`${category}-step-confirm`}>
          <div className="p-3 rounded-2xl bg-[#0a0a0a] border border-white/5">
            <p className="text-xs text-white/40">{selectedSvc.name}</p>
            <p className="text-lg font-bold" style={{ color: accent }}>{displayPrice(selectedSvc)}</p>
            <p className="text-xs text-white/55">
              {new Date(time).toLocaleString(language === 'sq' ? 'sq-AL' : (language === 'it' ? 'it-IT' : 'en-US'))}
            </p>
          </div>
          <Input placeholder="Emri i plotë" value={name}
                 onChange={(e) => setName(e.target.value)}
                 className="h-11 bg-[#050505] border-white/10 rounded-xl text-sm"
                 data-testid={`${category}-name`} />
          <Input placeholder="Numri WhatsApp (+355…)" value={phone}
                 onChange={(e) => setPhone(e.target.value)}
                 className="h-11 bg-[#050505] border-white/10 rounded-xl text-sm"
                 data-testid={`${category}-phone`} />
          <Input placeholder="Targa" value={plate}
                 onChange={(e) => setPlate(e.target.value.toUpperCase())}
                 className="h-11 bg-[#050505] border-white/10 rounded-xl text-sm font-mono"
                 data-testid={`${category}-plate`} />
          <Input placeholder="Shënime (opsionale)" value={notes}
                 onChange={(e) => setNotes(e.target.value)}
                 className="h-11 bg-[#050505] border-white/10 rounded-xl text-sm"
                 data-testid={`${category}-notes`} />
          <p className="text-[10px] text-white/35 text-center">
            Do të konfirmoheni dhe kujtoheni në WhatsApp
          </p>
          <Button onClick={submit} disabled={submitting}
                  data-testid={`${category}-confirm-btn`}
                  className="cash-cta-animated w-full text-white h-12 rounded-xl text-sm font-semibold"
                  style={{ background: accent }}>
            {submitting ? '…' : 'Konfirmo rezervimin'}
          </Button>
        </div>
      )}

      <BottomNav />
    </div>
  );
}
